import { SelectInput, SelectItem } from '@/shared/components';
import type { AccountOption } from '../model';
import { useTranslation } from 'react-i18next';
import { formatReceivedAmount } from '@/shared/lib';

type Props = {
  selectedAccount: string | undefined;
  setSelectedAccount: (account: string) => void;
  accounts: AccountOption[];
};

export const AccountSelectInput = ({
  selectedAccount,
  setSelectedAccount,
  accounts,
}: Props) => {
  const { t } = useTranslation();

  return (
    <SelectInput
      value={selectedAccount}
      onValueChange={setSelectedAccount}
      triggerClassName="bg-background shadow-smooth pl-[1.6rem] pr-[2rem] data-[state=open]:bg-stroke data-[state=open]:shadow-none [&>svg]:text-secondary [&>svg]:stroke-[1.5]"
      selectContentClassname="w-[var(--radix-select-trigger-width)] border-0 p-[1.2rem] shadow-smooth"
      classNameViewport="flex h-auto min-w-0 flex-col gap-[0.4rem] p-0"
      sideOffset={8}
      floatingLabel={t('webApiTokens.accountSelectPlaceholder')}
      options={accounts || []}
      valueKey="accountNumber"
      selectedKey="accountNumber"
      renderSelected={option => {
        const account = option as AccountOption;

        return (
          <div className="flex items-center gap-[0.8rem]">
            <span className="text-primary p3">{account.title}</span>
            <span className="text-secondary p3">
              {account.tradingAccKey ?? ''}
            </span>
          </div>
        );
      }}
      customOptionsRender={option => {
        const account = option as AccountOption;
        const Icon = account.icon;

        return (
          <SelectItem
            value={account.accountNumber}
            key={account.accountNumber}
            showOptionCheckmark={false}
            className="px-[1.2rem] py-[0.8rem] h-auto rounded-[1.6rem] min-h-[6rem] cursor-pointer focus:bg-secondary-bg">
            <div className="flex items-center gap-[1.6rem]">
              <div className="flex items-center justify-center size-[4.4rem] shrink-0 rounded-full border border-stroke">
                <Icon className="size-[2.4rem] text-primary" />
              </div>

              <div className="flex flex-col justify-center flex-1">
                <div className="flex items-center gap-[0.8rem]">
                  <span className="headline4 text-primary">
                    {account.title}
                  </span>
                  {account.tradingAccKey && (
                    <span className="headline4 text-secondary">
                      {account.tradingAccKey}
                    </span>
                  )}
                </div>

                <span className="text-secondary p5">
                  {formatReceivedAmount(account.balance)} {account.baseCode}
                </span>
              </div>
            </div>
          </SelectItem>
        );
      }}
    />
  );
};
